import { create } from 'zustand'

import { callApiNew } from 'api/axios'
import {
  formatAirPopulationCardInfo,
  formatAirPopulationDataResult,
} from 'api/dataTransform/airPopulation'
import { PATHS } from 'api/paths'
import { AirPopulationResultApi } from 'interfaces/types/AirPopulation'
import { getLocation } from 'utils/getCurrentLocation'

import { checkValidDataApi } from './helpers'
import { AirPopulationStoreStateType } from './types'

export const useAirPopulationStore = create<AirPopulationStoreStateType>(
  (set, get) => ({
    loading: false,
    error: null,
    currentData: null,
    cardInfo: null,
    listProvinces: null,
    currentLocation: null,
    setCurrentLocation(data) {
      set({ currentLocation: data })
    },
    fetchInitData: async () => {
      const { loading, listProvinces } = get()
      if (loading || listProvinces) return

      set({ loading: true, error: null })
      try {
        const currentGeoData = await getLocation(false)
        const currentGeoLocation = currentGeoData?.location || null
        const [
          infoRes,
          curentLocationRes,
          defaultResultRes,
          provincesRes,
        ] = await Promise.all([
          callApiNew(PATHS.HEALTH_TOOL.GET_AIR_POPULATION_INFO),
          currentGeoLocation
            ? callApiNew(
                PATHS.HEALTH_TOOL.GET_AIR_POPULATION_SPECIFIC_PROVINCE,
                {
                  params: {
                    type: 'geo',
                    lat: currentGeoLocation.latitude,
                    lng: currentGeoLocation.longitude,
                  },
                },
              )
            : null,
          callApiNew(PATHS.HEALTH_TOOL.GET_AIR_POPULATION_DEFAULT),
          callApiNew(PATHS.HEALTH_TOOL.GET_AIR_POPULATION_PROVINCE),
          getLocation(false),
        ])
        const currentLocationData = curentLocationRes?.data as AirPopulationResultApi
        const defaultData = defaultResultRes?.data as AirPopulationResultApi
        const isValidCurrentData = checkValidDataApi(currentLocationData, true)
        const isValidDefaultData = checkValidDataApi(defaultData)
        const isAllSuccess =
          !!infoRes?.data &&
          !!provincesRes?.data?.provinces &&
          (isValidCurrentData || isValidDefaultData)

        if (isAllSuccess) {
          const hasCurrentData = currentGeoLocation && isValidCurrentData
          set({
            currentLocation: hasCurrentData ? currentGeoData : null,
            cardInfo: formatAirPopulationCardInfo(infoRes?.data),
            currentData: formatAirPopulationDataResult(
              hasCurrentData
                ? currentLocationData
                : isValidDefaultData
                ? defaultData
                : null,
            ),
            listProvinces: provincesRes?.data?.provinces || [],
          })
        } else {
          set({ error: 'General error' })
        }
      } catch (err: any) {
        set({ error: err.message })
      } finally {
        set({ loading: false })
      }
    },
    retryFetchInitData: async () => {
      set({ error: null })
      await get().fetchInitData()
    },
  }),
)
